// src/pages/onboarding-screens/not_vehicle_owner_form.js
import React, { useMemo, useState, useContext, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  TextInput,
  Image,
  ScrollView,
  Platform,
} from "react-native";
import * as DocumentPicker from "expo-document-picker";
import ConsentModal from "../../components/ConsentModal";
import { AppContext } from "../../../App";
import { useFormNavigation } from "../../utils/navigationUtils";
import Header from "../../components/Header";
import { useTranslation } from "../../i18n";
import ProcurationModal from "../../components/ProcurationModal";
import { useFileValidation } from "../../hooks/useFileValidation";
import FileSizeErrorModal from "../../components/FileSizeErrorModal";

const COLORS = {
  primary: "#1CA7AE",
  primarySoft: "#E7F6F7",
  background: "#F7F9FA",
  textDark: "#1C1C1E",
  textMuted: "#6B7280",
  border: "#E5E7EB",
  error: "#DC2626",
};

function CheckRow({ label, checked, onPress }) {
  return (
    <Pressable onPress={onPress} style={styles.checkRow}>
      <View style={[styles.checkBox, checked && styles.checkBoxOn]}>
        {checked && <Text style={styles.checkMark}>✓</Text>}
      </View>
      <Text style={styles.checkLabel}>{label}</Text>
    </Pressable>
  );
}

/**
 * ✅ Validation rules (allow typing ANYTHING, but show error when non-empty + invalid)
 * - Person name: letters + space + hyphen + apostrophe. Must contain >=2 letters.
 * - Company name: letters/numbers + space + ' & . - . Must contain >=1 letter/number, length >=2.
 */
const isValidPersonName = (raw) => {
  const v = String(raw ?? "").trim();
  if (!v) return false;
  const letters = (v.match(/[A-Za-zÀ-ÖØ-öø-ÿ]/g) || []).length;
  if (letters < 2) return false;
  if (!/^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/.test(v)) return false;
  if (/\s{2,}/.test(v)) return false;
  return true;
};

const isValidCompanyName = (raw) => {
  const v = String(raw ?? "").trim();
  if (!v) return false;
  if (v.length < 2) return false;
  if (!/^[A-Za-zÀ-ÖØ-öø-ÿ0-9'&.\- ]+$/.test(v)) return false;
  if (!/[A-Za-zÀ-ÖØ-öø-ÿ0-9]/.test(v)) return false;
  if (/\s{2,}/.test(v)) return false;
  return true;
};

export default function NotVehicleOwnerForm({ navigation }) {
  const { verification, setVerification, setFormProgress } = useContext(AppContext);
  const [selectedFile, setSelectedFile] = useState(
    verification?.pendingUploads?.procurationFile || null
  );
  const { updateCurrentStep } = useFormNavigation();
  const { t } = useTranslation();

  const [showConsentModal, setShowConsentModal] = useState(false);

  const [driverFirstName, setDriverFirstName] = useState(
    verification?.driver?.firstName || ""
  );
  const [driverLastName, setDriverLastName] = useState(
    verification?.driver?.lastName || ""
  );

  const [ownerType, setOwnerType] = useState(verification?.owner?.type || null);

  const [ownerFirstName, setOwnerFirstName] = useState(
    verification?.owner?.firstName || ""
  );
  const [ownerLastName, setOwnerLastName] = useState(
    verification?.owner?.lastName || ""
  );
  const [ownerOrganizationName, setOwnerOrganizationName] = useState(
    verification?.owner?.organizationName || ""
  );

  const [selectedFileName, setSelectedFileName] = useState(
    verification?.owner?.procurationFileName || ""
  );

  // ✅ Skip document checkbox
  const [skipDocument, setSkipDocument] = useState(
    verification?.owner?.skipProcuration === true
  );

  // ✅ Track "touched" so we only show errors after blur (or after a submit attempt if you want later)
  const [touched, setTouched] = useState({
    driverFirst: false,
    driverLast: false,
    ownerFirst: false,
    ownerLast: false,
    company: false,
  });

  const [showProcurationModal, setShowProcurationModal] = useState(false);
  const { validateFile, formatFileSize } = useFileValidation();

  const [showFileErrorModal, setShowFileErrorModal] = useState(false);
  const [invalidFile, setInvalidFile] = useState(null);
  const [fileErrorMessage, setFileErrorMessage] = useState("");

  // useEffect(() => {
  //   updateCurrentStep("not_vehicle_owner_form_screen");
  // }, [updateCurrentStep]);
  useEffect(() => {
    setFormProgress((prev) => {
      if (prev?.currentStep === "not_vehicle_owner_form_screen") return prev;
      return { ...prev, currentStep: "not_vehicle_owner_form_screen" };
    });
  }, [setFormProgress]);


  // Auto-save in context (do NOT block user typing; store raw)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!driverFirstName && !driverLastName && !ownerFirstName && !ownerLastName && !ownerOrganizationName) {
        // still allow empty, but no need to spam context
        // (keep as-is)
      }

      setVerification((prev) => ({
        ...prev,
        driver: {
          firstName: driverFirstName,
          lastName: driverLastName,
        },
        owner:
          ownerType === "PHYSIQUE"
            ? {
                type: "PHYSIQUE",
                firstName: ownerFirstName,
                lastName: ownerLastName,
                procurationFileName: skipDocument ? null : selectedFileName,
                skipProcuration: skipDocument,
              }
            : ownerType === "MORALE"
            ? {
                type: "MORALE",
                organizationName: ownerOrganizationName,
                procurationFileName: skipDocument ? null : selectedFileName,
                skipProcuration: skipDocument,
              }
            : null,
      }));
    }, 600);

    return () => clearTimeout(timeout);
  }, [
    driverFirstName,
    driverLastName,
    ownerType,
    ownerFirstName,
    ownerLastName,
    ownerOrganizationName,
    selectedFileName,
    skipDocument,
    setVerification,
  ]);

  const isOwnerPhysique = ownerType === "PHYSIQUE";
  const isOwnerMorale = ownerType === "MORALE";

  // ✅ Errors: show ONLY when (touched && non-empty && invalid)
  const errDriverFirst =
    touched.driverFirst &&
    driverFirstName.trim().length > 0 &&
    !isValidPersonName(driverFirstName);

  const errDriverLast =
    touched.driverLast &&
    driverLastName.trim().length > 0 &&
    !isValidPersonName(driverLastName);

  const errOwnerFirst =
    isOwnerPhysique &&
    touched.ownerFirst &&
    ownerFirstName.trim().length > 0 &&
    !isValidPersonName(ownerFirstName);

  const errOwnerLast =
    isOwnerPhysique &&
    touched.ownerLast &&
    ownerLastName.trim().length > 0 &&
    !isValidPersonName(ownerLastName);

  const errCompany =
    isOwnerMorale &&
    touched.company &&
    ownerOrganizationName.trim().length > 0 &&
    !isValidCompanyName(ownerOrganizationName);

  // ✅ Validity (empty is invalid — so button stays disabled)
  const validDriverFirst = isValidPersonName(driverFirstName);
  const validDriverLast = isValidPersonName(driverLastName);

  const validOwnerFirst = isOwnerPhysique ? isValidPersonName(ownerFirstName) : true;
  const validOwnerLast = isOwnerPhysique ? isValidPersonName(ownerLastName) : true;

  const validCompany = isOwnerMorale ? isValidCompanyName(ownerOrganizationName) : true;

  const validUpload = skipDocument ? true : !!selectedFileName;

  const allFieldsValid =
    !!ownerType &&
    validDriverFirst &&
    validDriverLast &&
    (isOwnerPhysique ? validOwnerFirst && validOwnerLast : true) &&
    (isOwnerMorale ? validCompany : true) &&
    validUpload;

  // const handlePickDocument = async () => {
  //   if (skipDocument) return;

  //   if (Platform.OS === "web") {
  //     const input = document.createElement("input");
  //     input.type = "file";
  //     input.accept = ".pdf,image/*";
  //     input.onchange = (e) => {
  //       const file = e.target.files?.[0];
  //       if (file) setSelectedFileName(file.name);
  //     };
  //     input.click();
  //     return;
  //   }

  //   const result = await DocumentPicker.getDocumentAsync({
  //     type: ["application/pdf", "image/*"],
  //   });

  //   if (result.assets?.[0]) {
  //     setSelectedFileName(result.assets[0].name);
  //   }
  // };

  // Update the file picker
// const handlePickDocument = async () => {
//   if (skipDocument) return;

//   if (Platform.OS === "web") {
//     const input = document.createElement("input");
//     input.type = "file";
//     input.accept = ".pdf,image/*";
//     input.onchange = (e) => {
//       const file = e.target.files?.[0];
//       if (file) {
//         setSelectedFileName(file.name);
//         // ✅ Store the file object for web
//         setSelectedFile({
//           __webFile: file,
//           name: file.name,
//           type: file.type || "application/octet-stream",
//           mimeType: file.type || "application/octet-stream",
//         });
//       }
//     };
//     input.click();
//     return;
//   }

//   const result = await DocumentPicker.getDocumentAsync({
//     type: ["application/pdf", "image/*"],
//   });

//   if (result.assets?.[0]) {
//     const asset = result.assets[0];
//     setSelectedFileName(asset.name);
//     // ✅ Store the file object for native
//     setSelectedFile({
//       uri: asset.uri,
//       name: asset.name,
//       type: asset.mimeType || "application/octet-stream",
//       mimeType: asset.mimeType || "application/octet-stream",
//     });
//   }
// };

 // Update handlePickDocument:
const handlePickDocument = async () => {
  if (skipDocument) return;

  if (Platform.OS === "web") {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,image/*";
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        // Check file size (15MB limit)
        if (file.size > 15 * 1024 * 1024) {
          setInvalidFile({
            name: file.name,
            size: formatFileSize(file.size)
          });
          setFileErrorMessage(t('fileValidation.fileTooLarge', { 
            maxSize: '15MB',
            fileName: file.name 
          }));
          setShowFileErrorModal(true);
          return;
        }
        
        setSelectedFileName(file.name);
        setSelectedFile({
          __webFile: file,
          name: file.name,
          size: file.size,
          type: file.type || "application/octet-stream",
          mimeType: file.type || "application/octet-stream",
        });
      }
    };
    input.click();
    return;
  }

  const result = await DocumentPicker.getDocumentAsync({
    type: ["application/pdf", "image/*"],
  });

  if (result.assets?.[0]) {
    const asset = result.assets[0];
    
    // Check file size
    if (asset.size > 15 * 1024 * 1024) {
      setInvalidFile({
        name: asset.name,
        size: formatFileSize(asset.size)
      });
      setFileErrorMessage(t('fileValidation.fileTooLarge', { 
        maxSize: '15MB',
        fileName: asset.name 
      }));
      setShowFileErrorModal(true);
      return;
    }
    
    setSelectedFileName(asset.name);
    setSelectedFile({
      uri: asset.uri,
      name: asset.name,
      size: asset.size,
      type: asset.mimeType || "application/octet-stream",
      mimeType: asset.mimeType || "application/octet-stream",
    });
  }
};

// Update the auto-save useEffect
// useEffect(() => {
//   const timeout = setTimeout(() => {
//     setVerification((prev) => ({
//       ...prev,
//       driver: {
//         firstName: driverFirstName,
//         lastName: driverLastName,
//       },
//       owner:
//         ownerType === "PHYSIQUE"
//           ? {
//               type: "PHYSIQUE",
//               firstName: ownerFirstName,
//               lastName: ownerLastName,
//               procurationFileName: skipDocument ? null : selectedFileName,
//               skipProcuration: skipDocument,
//               // ✅ Also store file object in owner if needed
//               procurationFile: skipDocument ? null : selectedFile,
//             }
//           : ownerType === "MORALE"
//           ? {
//               type: "MORALE",
//               organizationName: ownerOrganizationName,
//               procurationFileName: skipDocument ? null : selectedFileName,
//               skipProcuration: skipDocument,
//               // ✅ Also store file object in owner if needed
//               procurationFile: skipDocument ? null : selectedFile,
//             }
//           : null,
//       // ✅ Store the file in pendingUploads as well (for easier access)
//       pendingUploads: {
//         ...(prev.pendingUploads || {}),
//         procurationFile: skipDocument ? null : selectedFile,
//       },
//     }));
//   }, 600);

//   return () => clearTimeout(timeout);
// }, [
//   driverFirstName,
//   driverLastName,
//   ownerType,
//   ownerFirstName,
//   ownerLastName,
//   ownerOrganizationName,
//   selectedFileName,
//   selectedFile, // ✅ Add this
//   skipDocument,
//   setVerification,
// ]);

// Fix the auto-save useEffect - remove references to non-existent variables

useEffect(() => {
  const timeout = setTimeout(() => {
    // Build driver info
    const driverFullName = `${driverFirstName.trim()} ${driverLastName.trim()}`.trim();
    
    // Build owner info based on type - ONLY use variables that exist in your component
    let ownerData = {};
    if (ownerType === "PHYSIQUE") {
      ownerData = {
        type: "PHYSIQUE",
        firstName: ownerFirstName.trim(),
        lastName: ownerLastName.trim(),
        fullName: `${ownerFirstName.trim()} ${ownerLastName.trim()}`.trim(),
        // Remove references to non-existent variables
        // gender: ownerGender, - THIS DOESN'T EXIST
        // phone: ownerPhone, - THIS DOESN'T EXIST
        // email: ownerEmail, - THIS DOESN'T EXIST
        // cinNumber: ownerCin, - THIS DOESN'T EXIST
        // taxpayerNumber: ownerTaxpayerNumber, - THIS DOESN'T EXIST
        // ownerSyndicate: ownerSyndicate, - THIS DOESN'T EXIST
        skipProcuration: skipDocument,
        procurationFileName: skipDocument ? null : selectedFileName,
      };
    } else if (ownerType === "MORALE") {
      ownerData = {
        type: "MORALE",
        organizationName: ownerOrganizationName.trim(),
        // Remove references to non-existent variables
        // phone: ownerPhone, - THIS DOESN'T EXIST
        // email: ownerEmail, - THIS DOESN'T EXIST
        // taxpayerNumber: ownerTaxpayerNumber, - THIS DOESN'T EXIST
        // ownerSyndicate: ownerSyndicate, - THIS DOESN'T EXIST
        skipProcuration: skipDocument,
        procurationFileName: skipDocument ? null : selectedFileName,
      };
    }

    setVerification((prev) => ({
      ...prev,
      driver: {
        ...prev.driver,
        firstName: driverFirstName.trim(),
        lastName: driverLastName.trim(),
        fullName: driverFullName,
      },
      owner: {
        ...prev.owner,
        ...ownerData,
      },
      pendingUploads: {
        ...(prev.pendingUploads || {}),
        procurationFile: skipDocument ? null : selectedFile,
      },
    }));
  }, 600);

  return () => clearTimeout(timeout);
}, [
  driverFirstName, 
  driverLastName,
  ownerType, 
  ownerFirstName, 
  ownerLastName, 
  ownerOrganizationName,
  // Remove all the non-existent variables from dependencies
  // ownerGender, ownerPhone, ownerEmail, ownerCin, ownerTaxpayerNumber, ownerSyndicate,
  selectedFileName, 
  selectedFile, 
  skipDocument, 
  setVerification
]);

  // const handleContinue = () => {
  //   if (!allFieldsValid) return;

  //   // ✅ Save final trimmed values
  //   setVerification((prev) => ({
  //     ...prev,
  //     driver: {
  //       firstName: driverFirstName.trim(),
  //       lastName: driverLastName.trim(),
  //     },
  //     owner:
  //       ownerType === "PHYSIQUE"
  //         ? {
  //             type: "PHYSIQUE",
  //             firstName: ownerFirstName.trim(),
  //             lastName: ownerLastName.trim(),
  //             procurationFileName: skipDocument ? null : selectedFileName,
  //             skipProcuration: skipDocument,
  //           }
  //         : {
  //             type: "MORALE",
  //             organizationName: ownerOrganizationName.trim(),
  //             procurationFileName: skipDocument ? null : selectedFileName,
  //             skipProcuration: skipDocument,
  //           },
  //   }));

  //   setFormProgress((prev) => ({
  //     ...prev,
  //     completedSteps: [
  //       ...new Set([...prev.completedSteps, "not_vehicle_owner_form_screen"]),
  //     ],
  //   }));

  //   setShowConsentModal(true);
  // };

  // Update handleContinue to show modal instead of directly navigating
const handleContinue = () => {
  if (!allFieldsValid) return;

  // 🟢 DRIVER WORKFLOW - Check if procuration is missing
  const isDriverWorkflow = verification?.actorRole === "DRIVER";
  const hasProcurationFile = !skipDocument && selectedFile;
  
  if (isDriverWorkflow && !hasProcurationFile) {
    // Show modal to explain why procuration is needed
    setShowProcurationModal(true);
    return;
  }

  // OWNER WORKFLOW or DRIVER with procuration - proceed normally
  proceedToConsent();
};

// New function to proceed to consent
const proceedToConsent = () => {
  // Save final trimmed values
  setVerification((prev) => ({
    ...prev,
    driver: {
      firstName: driverFirstName.trim(),
      lastName: driverLastName.trim(),
    },
    owner:
      ownerType === "PHYSIQUE"
        ? {
            type: "PHYSIQUE",
            firstName: ownerFirstName.trim(),
            lastName: ownerLastName.trim(),
            procurationFileName: skipDocument ? null : selectedFileName,
            skipProcuration: skipDocument,
          }
        : {
            type: "MORALE",
            organizationName: ownerOrganizationName.trim(),
            procurationFileName: skipDocument ? null : selectedFileName,
            skipProcuration: skipDocument,
          },
  }));

  setFormProgress((prev) => ({
    ...prev,
    completedSteps: [
      ...new Set([...prev.completedSteps, "not_vehicle_owner_form_screen"]),
    ],
  }));

  setShowConsentModal(true);
};
  return (
    <View style={styles.screen}>
      <Header />

      <ScrollView contentContainerStyle={styles.scroll}>
        <Pressable
          style={styles.backButton}
          onPress={() => navigation.navigate("ownership_screen")}
        >
          <Text style={styles.backIcon}>←</Text>
        </Pressable>

        <Text style={styles.title}>{t("nonOwnerForm.title")}</Text>
        <Text style={styles.subtitle}>{t("nonOwnerForm.subtitle")}</Text>

        {/* DRIVER */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>{t("nonOwnerForm.driver")}</Text>

          <TextInput
            style={styles.input}
            placeholder={t("nonOwnerForm.driverFirstNamePlaceholder")}
            placeholderTextColor="#9CA3AF"
            value={driverFirstName}
            onChangeText={setDriverFirstName} // ✅ allow anything
            onBlur={() => setTouched((p) => ({ ...p, driverFirst: true }))}
            autoCorrect={false}
          />
          {errDriverFirst ? (
            <Text style={styles.errorText}>{t("errors.invalidName")}</Text>
          ) : null}

          <TextInput
            style={styles.input}
            placeholder={t("nonOwnerForm.driverLastNamePlaceholder")}
            placeholderTextColor="#9CA3AF"
            value={driverLastName}
            onChangeText={setDriverLastName} // ✅ allow anything
            onBlur={() => setTouched((p) => ({ ...p, driverLast: true }))}
            autoCorrect={false}
          />
          {errDriverLast ? (
            <Text style={styles.errorText}>{t("errors.invalidName")}</Text>
          ) : null}
        </View>

        {/* OWNER */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>{t("nonOwnerForm.owner")}</Text>

          <View style={styles.segment}>
            <Pressable
              style={[
                styles.segmentItem,
                ownerType === "PHYSIQUE" && styles.segmentActive,
              ]}
              onPress={() => setOwnerType("PHYSIQUE")}
            >
              <Text
                style={[
                  styles.segmentText,
                  ownerType === "PHYSIQUE" && styles.segmentTextActive,
                ]}
              >
                {t("nonOwnerForm.physicalPerson")}
              </Text>
            </Pressable>

            <Pressable
              style={[
                styles.segmentItem,
                ownerType === "MORALE" && styles.segmentActive,
              ]}
              onPress={() => setOwnerType("MORALE")}
            >
              <Text
                style={[
                  styles.segmentText,
                  ownerType === "MORALE" && styles.segmentTextActive,
                ]}
              >
                {t("nonOwnerForm.company")}
              </Text>
            </Pressable>
          </View>

          {isOwnerPhysique && (
            <>
              <TextInput
                style={styles.input}
                placeholder={t("nonOwnerForm.ownerFirstNamePlaceholder")}
                placeholderTextColor="#9CA3AF"
                value={ownerFirstName}
                onChangeText={setOwnerFirstName} // ✅ allow anything
                onBlur={() => setTouched((p) => ({ ...p, ownerFirst: true }))}
                autoCorrect={false}
              />
              {errOwnerFirst ? (
               <Text style={styles.errorText}>{t("errors.invalidName")}</Text>

              ) : null}

              <TextInput
                style={styles.input}
                placeholder={t("nonOwnerForm.ownerLastNamePlaceholder")}
                placeholderTextColor="#9CA3AF"
                value={ownerLastName}
                onChangeText={setOwnerLastName} // ✅ allow anything
                onBlur={() => setTouched((p) => ({ ...p, ownerLast: true }))}
                autoCorrect={false}
              />
              {errOwnerLast ? (
               <Text style={styles.errorText}>{t("errors.invalidName")}</Text>

              ) : null}
            </>
          )}

          {isOwnerMorale && (
            <>
              <TextInput
                style={styles.input}
                placeholder={t("nonOwnerForm.companyNamePlaceholder")}
                placeholderTextColor="#9CA3AF"
                value={ownerOrganizationName}
                onChangeText={setOwnerOrganizationName} // ✅ allow anything
                onBlur={() => setTouched((p) => ({ ...p, company: true }))}
                autoCorrect={false}
              />
              {errCompany ? (
               <Text style={styles.errorText}>{t("errors.invalidName")}</Text>
              ) : null}
            </>
          )}
        </View>

        {/* UPLOAD */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>{t("nonOwnerForm.procuration")}</Text>

          <CheckRow
            label={t("documentUpload.skip")}
            checked={skipDocument}
            onPress={() => {
              setSkipDocument((v) => !v);
              if (!skipDocument) setSelectedFileName("");
            }}
          />

          {!skipDocument && (
            <Pressable style={styles.uploadBox} onPress={handlePickDocument}>
              <Image
                source={require("../../../assets/file_upload.png")}
                style={styles.uploadIcon}
              />
              <Text style={styles.uploadText}>
                {selectedFileName || t("nonOwnerForm.addDocument")}
              </Text>
              <Text style={styles.uploadHint}>
                {t("nonOwnerForm.fileFormats")}
              </Text>
            </Pressable>
          )}
        </View>

        <Pressable
          disabled={!allFieldsValid}
          onPress={handleContinue}
          style={[styles.cta, { opacity: allFieldsValid ? 1 : 0.5 }]}
        >
          <Text style={styles.ctaText}>{t("common.continue")}</Text>
        </Pressable>
      </ScrollView>

      {showConsentModal && (
        <ConsentModal
          onDismiss={() => setShowConsentModal(false)}
          navigation={navigation}
        />
      )}
      {showProcurationModal && (
        <ProcurationModal
          visible={showProcurationModal}
          onDismiss={() => setShowProcurationModal(false)}
          onContinueWithout={() => {
            setShowProcurationModal(false);
            setSkipDocument(true);
            setSelectedFile(null);
            setSelectedFileName("");
            // Small delay to ensure state updates
            setTimeout(() => proceedToConsent(), 100);
          }}
          onUploadDocument={() => {
            setShowProcurationModal(false);
            // Open file picker
            handlePickDocument();
          }}
        />
      )}
      <FileSizeErrorModal
        visible={showFileErrorModal}
        onDismiss={() => setShowFileErrorModal(false)}
        errorMessage={fileErrorMessage}
        fileName={invalidFile?.name}
        fileSize={invalidFile?.size}
        onRetry={() => {
          setShowFileErrorModal(false);
          setInvalidFile(null);
          handlePickDocument();
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: COLORS.background },
  scroll: { paddingHorizontal: 16, paddingBottom: 40 },
  backButton: {
    width: 42,
    height: 42,
    borderRadius: 12,
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
    marginTop: 16,
  },
  backIcon: { fontSize: 18, color: COLORS.textDark },
  title: {
    fontSize: 24,
    fontWeight: "800",
    color: COLORS.textDark,
    textAlign: "center",
    marginBottom: 4,
  },
  subtitle: {
    textAlign: "center",
    color: COLORS.textMuted,
    marginBottom: 24,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 14,
    padding: 16,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 3,
  },
  cardTitle: { fontSize: 16, fontWeight: "700", marginBottom: 12 },
  input: {
    borderWidth: 1,
    borderColor: COLORS.border, // ✅ no red border
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    marginBottom: 8,
    backgroundColor: "#FAFAFA",
    color: COLORS.textDark,
  },
  errorText: {
    color: COLORS.error,
    fontSize: 12,
    fontWeight: "700",
    marginBottom: 12,
  },
  segment: {
    flexDirection: "row",
    backgroundColor: COLORS.primarySoft,
    borderRadius: 999,
    padding: 4,
    marginBottom: 12,
  },
  segmentItem: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 999,
    alignItems: "center",
  },
  segmentActive: { backgroundColor: COLORS.primary },
  segmentText: { fontSize: 13, fontWeight: "600", color: COLORS.textMuted },
  segmentTextActive: { color: "#FFFFFF" },

  uploadBox: {
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: COLORS.primary,
    borderRadius: 12,
    padding: 16,
    alignItems: "center",
  },
  uploadIcon: { width: 36, height: 36, marginBottom: 8 },
  uploadText: { fontWeight: "600", color: COLORS.textDark },
  uploadHint: { fontSize: 12, color: COLORS.textMuted },

  checkRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginBottom: 12,
  },
  checkBox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: COLORS.border,
    alignItems: "center",
    justifyContent: "center",
  },
  checkBoxOn: { backgroundColor: COLORS.primary, borderColor: COLORS.primary },
  checkMark: { color: "#fff", fontWeight: "900" },
  checkLabel: { fontWeight: "600", color: COLORS.textDark },

  cta: {
    backgroundColor: COLORS.primary,
    borderRadius: 14,
    paddingVertical: 14,
    alignItems: "center",
    marginTop: 16,
  },
  ctaText: { color: "#FFFFFF", fontSize: 16, fontWeight: "700" },
});
